import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { EmailApiRequest, EmailApiResponse } from '../types/email-api.types';

/**
 * Configuration for the Email API service
 */
export type EmailApiConfig = {
  baseUrl: string;
};

export const EMAIL_API_CONFIG = new InjectionToken<EmailApiConfig>(
  'EmailApiConfig'
);

/**
 * Service for interacting with the disposable email API
 */
@Injectable({
  providedIn: 'root',
})
export class EmailApiService {
  private baseUrl: string;
  private headers: HttpHeaders;

  /**
   * Creates an instance of the EmailApiService
   * @param http - Angular's HttpClient for making HTTP requests
   * @param config - Configuration object for the API
   */
  constructor(
    private http: HttpClient,
    @Inject(EMAIL_API_CONFIG) private config: EmailApiConfig
  ) {
    this.baseUrl = config.baseUrl;
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  /**
   * Creates a disposable email address
   * @param request - The email creation request data
   * @returns Observable with the API response
   */
  createDisposableEmail(
    request: EmailApiRequest
  ): Observable<EmailApiResponse> {
    return this.http
      .post<EmailApiResponse>(`${this.baseUrl}/email`, request, {
        headers: this.headers,
      })
      .pipe(
        catchError((error) => throwError(() => new Error(`Failed to create disposable email: ${error.message}`)))
      );
  }

  /**
   * Gets a disposable email address by ID
   * @param id - The ID of the disposable email address
   * @returns Observable with the API response
   */
  getDisposableEmail(id: string): Observable<EmailApiResponse> {
    return this.http
      .get<EmailApiResponse>(`${this.baseUrl}/email?id=${id}`, {
        headers: this.headers,
      })
      .pipe(
        catchError((error) => throwError(() => new Error(`Failed to get disposable email: ${error.message}`)))
      );
  }
}
